import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiActivity, FiMessageCircle, FiSearch, FiCheckCircle } from 'react-icons/fi';
import NeonButton from '../../components/Common/Button';
import { useLanguage } from '../../context/LanguageContext';
import PhoneDemo from './PhoneDemo';
import './LandingPage.css';
import './LandingPage.light.css';
import './LandingPage.dark.css';

const landingCopy = {
  en: {
    caseSignals: ['DKA', 'TSH-oma', 'CKD', 'Liver transplant', 'Russian case'],
    brandSmall: 'Clinical Simulator',
    nav: ['Practice', 'Cases', 'Review', 'Subscription'],
    signIn: 'Sign in',
    getStarted: 'Get started',
    eyebrow: 'AI patient simulation for medical students',
    subtitle: 'Practice patient interviews like a real clinical encounter: start with a short chart, ask your own questions, search investigations, and defend your plan.',
    startTraining: 'Start training',
    openDashboard: 'Open dashboard',
    monitorTitle: 'Patient Interview',
    liveCase: 'Live case',
    patientLine: 'I feel hot and my heart has been racing lately.',
    studentLine: 'Any weight loss, tremor, or neck swelling?',
    endocrinePanel: 'Endocrine reasoning engine',
    endocrineMetricLabels: ['Glucose', 'TSH', 'Ketones'],
    axisLabels: ['Hypothalamus', 'Pituitary', 'Thyroid'],
    caseDeckTitle: 'Endocrinology case deck',
    caseDeckText: 'Dynamic cases around DKA, thyrotoxicosis, renal-endocrine overlap, metabolic risk, and medication safety.',
    evidenceTitle: 'Evidence appears when requested',
    feedbackTitle: 'Scored clinical judgement',
    reviewLead: 'A polished practice room for endocrine reasoning, not a static quiz.',
    workflow: [
      ['Interview', 'Ask focused questions without receiving the whole case at once.', FiMessageCircle],
      ['Investigate', 'Search labs and imaging when your differential needs evidence.', FiSearch],
      ['Decide', 'Submit diagnosis and management, then review the reasoning.', FiCheckCircle]
    ],
    labEyebrow: 'Built like a skills lab',
    labTitle: 'Not a symptom dump. A case you have to work.',
    panelKicker: 'Searchable investigations',
    panelTitle: 'Students choose what to request.',
    panelText: 'Labs stay available from the beginning, but results only appear after searching or opening a matching category, keeping the clinical reasoning active.',
    tryCase: 'Try a case',
    createAccount: 'Create account',
    reviewDetails: [
      ['For students', 'Timed sessions, searchable investigations, saved transcripts, and structured self-review.'],
      ['For doctors', 'Patient anketa editing, submission review, scoring, and feedback in one workspace.'],
      ['For groups', 'Subscription plans, cohort-ready review flow, and multilingual case practice.']
    ],
    workflowEyebrow: 'Clinical education workflow',
    workflowTitle: 'From first question to final feedback.',
    footerText: 'AI patient simulation for medical students, doctor review, and focused clinical reasoning practice.',
    footerColumns: [
      ['Platform', ['Patient interviews', 'Case dashboard', 'Lab reports', 'Doctor review']],
      ['Training', ['OSCE practice', 'Clinical reasoning', 'Endocrine cases', 'Multilingual cases']],
      ['Project', ['About MedSim', 'Accessibility', 'Privacy', 'Support']]
    ],
    footerBottom: ['© 2026 MedSim Clinical Simulator', 'Educational use only', 'Built for patient reasoning practice']
  },
  ru: {
    caseSignals: ['ДКА', 'ТТГ-ома', 'ХБП', 'Трансплантация печени', 'Случай на русском'],
    brandSmall: 'Клинический симулятор',
    nav: ['Практика', 'Случаи', 'Разбор', 'Подписка'],
    signIn: 'Войти',
    getStarted: 'Начать',
    eyebrow: 'Симуляция пациентов с ИИ для студентов-медиков',
    subtitle: 'Тренируйте беседу с пациентом как на реальном приёме: начните с краткой карты, задавайте свои вопросы, ищите исследования и защищайте план лечения.',
    startTraining: 'Начать тренировку',
    openDashboard: 'Открыть панель',
    monitorTitle: 'Приём пациента',
    liveCase: 'Живой случай',
    patientLine: 'Мне жарко, и сердце в последнее время сильно бьётся.',
    studentLine: 'Была потеря веса, тремор или отёк шеи?',
    endocrinePanel: 'Механика эндокринного мышления',
    endocrineMetricLabels: ['Глюкоза', 'ТТГ', 'Кетоны'],
    axisLabels: ['Гипоталамус', 'Гипофиз', 'Щитовидная'],
    caseDeckTitle: 'Колода эндокринных случаев',
    caseDeckText: 'Динамические случаи по ДКА, тиреотоксикозу, почечно-эндокринным связям, метаболическому риску и безопасности препаратов.',
    evidenceTitle: 'Данные появляются по запросу',
    feedbackTitle: 'Оценка клинического решения',
    reviewLead: 'Отточенная учебная среда для эндокринного мышления, а не статичный тест.',
    workflow: [
      ['Опрос', 'Задавайте точные вопросы, не получая весь случай сразу.', FiMessageCircle],
      ['Исследования', 'Ищите анализы и визуализацию, когда дифференциалу нужны доказательства.', FiSearch],
      ['Решение', 'Отправьте диагноз и план лечения, затем разберите рассуждение.', FiCheckCircle]
    ],
    labEyebrow: 'Как учебная клиническая лаборатория',
    labTitle: 'Не список симптомов. Случай, который нужно раскрыть.',
    panelKicker: 'Поиск исследований',
    panelTitle: 'Студенты сами выбирают, что запросить.',
    panelText: 'Исследования доступны с начала, но результаты появляются после поиска или открытия подходящей категории, чтобы клиническое мышление оставалось активным.',
    tryCase: 'Попробовать случай',
    createAccount: 'Создать аккаунт',
    reviewDetails: [
      ['Для студентов', 'Сеансы с таймером, поиск исследований, сохранённые переписки и структурированный саморазбор.'],
      ['Для врачей', 'Редактирование анкеты пациента, проверка работ, оценивание и обратная связь в одном пространстве.'],
      ['Для групп', 'Планы подписки, разбор для потока и многоязычная практика случаев.']
    ],
    workflowEyebrow: 'Учебный клинический процесс',
    workflowTitle: 'От первого вопроса до итогового разбора.',
    footerText: 'ИИ-симуляция пациентов для студентов-медиков, врачебный разбор и тренировка клинического мышления.',
    footerColumns: [
      ['Платформа', ['Опрос пациента', 'Панель случаев', 'Результаты анализов', 'Проверка врачом']],
      ['Обучение', ['Практика ОСКЭ', 'Клиническое мышление', 'Эндокринные случаи', 'Многоязычные случаи']],
      ['Проект', ['О MedSim', 'Доступность', 'Конфиденциальность', 'Поддержка']]
    ],
    footerBottom: ['© 2026 Клинический симулятор MedSim', 'Только для обучения', 'Создано для тренировки клинического мышления']
  },
  uz: {
    caseSignals: ['DKA', 'TSH-oma', 'SBK', 'Jigar transplantatsiyasi', 'Rus tilidagi holat'],
    brandSmall: 'Klinik simulyator',
    nav: ['Mashq', 'Holatlar', 'Tahlil', 'Obuna'],
    signIn: 'Kirish',
    getStarted: 'Boshlash',
    eyebrow: 'Tibbiyot talabalari uchun AI bemor simulyatsiyasi',
    subtitle: 'Bemor bilan suhbatni haqiqiy klinik qabul kabi mashq qiling: qisqa karta bilan boshlang, savollar bering, tekshiruvlarni qidiring va rejangizni asoslang.',
    startTraining: 'Mashqni boshlash',
    openDashboard: 'Panelni ochish',
    monitorTitle: 'Bemor qabuli',
    liveCase: 'Jonli holat',
    patientLine: 'So‘nggi paytda issiq bo‘lib ketyapman va yuragim tez uryapti.',
    studentLine: 'Vazn yo‘qotish, titroq yoki bo‘yinda shish bormi?',
    endocrinePanel: 'Endokrin fikrlash mexanizmi',
    endocrineMetricLabels: ['Glyukoza', 'TSH', 'Ketonlar'],
    axisLabels: ['Gipotalamus', 'Gipofiz', 'Qalqonsimon'],
    caseDeckTitle: 'Endokrin holatlar to‘plami',
    caseDeckText: 'DKA, tireotoksikoz, buyrak-endokrin bog‘liqligi, metabolik xavf va dori xavfsizligi bo‘yicha dinamik holatlar.',
    evidenceTitle: 'Dalillar so‘ralganda ochiladi',
    feedbackTitle: 'Klinik qaror baholanadi',
    reviewLead: 'Endokrin klinik fikrlash uchun puxta mashq muhiti, oddiy statik test emas.',
    workflow: [
      ['Suhbat', 'Butun holatni birdan olmay, maqsadli savollar bering.', FiMessageCircle],
      ['Tekshiruv', 'Differensial tashxisga dalil kerak bo‘lsa, tahlil va tasvirlarni qidiring.', FiSearch],
      ['Qaror', 'Tashxis va davolash rejasini yuboring, keyin fikrlashingizni ko‘rib chiqing.', FiCheckCircle]
    ],
    labEyebrow: 'Ko‘nikma laboratoriyasi kabi',
    labTitle: 'Bu tayyor simptomlar ro‘yxati emas. Holatni o‘zingiz ochasiz.',
    panelKicker: 'Qidiriladigan tekshiruvlar',
    panelTitle: 'Talabalar nimani so‘rashni o‘zlari tanlaydi.',
    panelText: 'Tekshiruvlar boshidan mavjud, lekin natijalar qidirilganda yoki mos kategoriya ochilganda ko‘rinadi. Shu bilan klinik fikrlash faol qoladi.',
    tryCase: 'Holatni sinash',
    createAccount: 'Hisob yaratish',
    reviewDetails: [
      ['Talabalar uchun', 'Taymerli seanslar, qidiriladigan tekshiruvlar, saqlangan suhbatlar va tuzilgan o‘z-o‘zini tahlil.'],
      ['Shifokorlar uchun', 'Bemor anketasini tahrirlash, topshiriqlarni tekshirish, baholash va fikr bildirish bir joyda.'],
      ['Guruhlar uchun', 'Obuna rejalari, guruh tahlili va ko‘p tilli klinik mashqlar.']
    ],
    workflowEyebrow: 'Klinik ta’lim jarayoni',
    workflowTitle: 'Birinchi savoldan yakuniy tahlilgacha.',
    footerText: 'Tibbiyot talabalari uchun AI bemor simulyatsiyasi, shifokor tahlili va klinik fikrlash mashqi.',
    footerColumns: [
      ['Platforma', ['Bemor suhbatlari', 'Holatlar paneli', 'Tahlil natijalari', 'Shifokor tekshiruvi']],
      ['Mashg‘ulot', ['OSCE mashqi', 'Klinik fikrlash', 'Endokrin holatlar', 'Ko‘p tilli holatlar']],
      ['Loyiha', ['MedSim haqida', 'Qulaylik', 'Maxfiylik', 'Yordam']]
    ],
    footerBottom: ['© 2026 MedSim klinik simulyatori', 'Faqat ta’lim uchun', 'Bemor bo‘yicha fikrlash mashqi uchun yaratilgan']
  }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const copy = landingCopy[language] || landingCopy.en;

  return (
    <div className="landing-container">
      <header className="landing-header">
        <button className="brand-mark" onClick={() => navigate('/')}>
          <span>MedSim</span>
          <small>{copy.brandSmall}</small>
        </button>

        <nav className="landing-nav" aria-label="Primary navigation">
          <a href="#studio">{copy.nav[0]}</a>
          <a href="#cases">{copy.nav[1]}</a>
          <a href="#review">{copy.nav[2]}</a>
          <button type="button" onClick={() => navigate('/subscription')}>{copy.nav[3]}</button>
        </nav>

        <div className="header-buttons">
          <button className="text-link" onClick={() => navigate('/login')}>{copy.signIn}</button>
          <NeonButton onClick={() => navigate('/register')}>{copy.getStarted}</NeonButton>
        </div>
      </header>

      <main>
        <section className="hero-section" id="studio">
          <div className="hero-backdrop" aria-hidden="true" />
          <div className="hero-scrim" />

          <div className="hero-copy">
            <p className="eyebrow">{copy.eyebrow}</p>
            <h1>MedSim</h1>
            <p className="hero-subtitle">{copy.subtitle}</p>
            <div className="hero-actions">
              <NeonButton onClick={() => navigate('/register')}>
                {copy.startTraining} <FiArrowRight size={18} />
              </NeonButton>
              <button className="secondary-action" onClick={() => navigate('/login')}>
                {copy.openDashboard}
              </button>
            </div>
          </div>

          <div className="hero-monitor" aria-label="Animated endocrine simulator preview">
            <div className="monitor-top">
              <span className="status-dot" />
              <strong>{copy.monitorTitle}</strong>
              <small>{copy.liveCase}</small>
            </div>
            <div className="ecg-line">
              <FiActivity size={22} />
              <div className="ecg-screen" aria-hidden="true">
                <svg className="ecg-wave" viewBox="0 0 420 80" preserveAspectRatio="none">
                  <polyline points="0,45 38,45 48,45 55,24 66,62 80,45 126,45 142,45 151,37 160,45 214,45 224,45 232,18 244,68 260,45 314,45 328,45 338,34 348,45 420,45" />
                  <polyline points="0,45 38,45 48,45 55,24 66,62 80,45 126,45 142,45 151,37 160,45 214,45 224,45 232,18 244,68 260,45 314,45 328,45 338,34 348,45 420,45" />
                </svg>
              </div>
            </div>
            <div className="monitor-chat patient">{copy.patientLine}</div>
            <div className="monitor-chat student">{copy.studentLine}</div>
            <div className="endocrine-metrics">
              {copy.endocrineMetricLabels.map((label, index) => (
                <span key={label} style={{ '--metric-step': index }}>
                  <small>{label}</small>
                  <strong>{['18.7', '0.01', '+++'][index]}</strong>
                </span>
              ))}
            </div>
            <div className="monitor-labs">
              {copy.caseSignals.map(signal => <span key={signal}>{signal}</span>)}
            </div>
          </div>
        </section>

        <section className="signal-strip" aria-label="Clinical workflow">
          {copy.workflow.map(([title, text, Icon]) => (
            <button key={title} onClick={() => navigate('/login')}>
              <Icon size={22} />
              <span>
                <strong>{title}</strong>
                <small>{text}</small>
              </span>
            </button>
          ))}
        </section>

        <section className="case-lab-section" id="cases">
          <div className="section-heading">
            <p className="eyebrow">{copy.labEyebrow}</p>
            <h2>{copy.labTitle}</h2>
          </div>
          <div className="lab-board">
            <PhoneDemo captions={copy.workflow.map(([title, description]) => ({ title, description }))} />
            <div className="lab-board-panel">
              <span className="panel-kicker">{copy.panelKicker}</span>
              <h3>{copy.panelTitle}</h3>
              <p>{copy.panelText}</p>
              <button onClick={() => navigate('/register')}>{copy.tryCase}</button>
            </div>
          </div>
        </section>

        <section className="review-section" id="review">
          <div>
            <p className="eyebrow">{copy.workflowEyebrow}</p>
            <h2>{copy.reviewLead}</h2>
          </div>
          <button onClick={() => navigate('/register')}>{copy.createAccount}</button>
        </section>

        <section className="closing-section" aria-label="Platform details">
          <div className="closing-grid">
            {copy.reviewDetails.map(([title, text]) => (
              <article key={title}>
                <strong>{title}</strong>
                <p>{text}</p>
              </article>
            ))}
          </div>
          <div className="closing-copy">
            <div className="workflow-sequence" aria-hidden="true">
              {copy.workflow.map(([title, , Icon], index) => <span key={title}><Icon /><small>0{index + 1}</small><strong>{title}</strong></span>)}
            </div>
            <p className="eyebrow">{copy.workflowEyebrow}</p>
            <h2>{copy.workflowTitle}</h2>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-brand">
            <strong>MedSim</strong>
            <p>{copy.footerText}</p>
            <button onClick={() => navigate('/register')}>
              {copy.startTraining} <FiArrowRight size={16} />
            </button>
          </div>

          {copy.footerColumns.map(([title, links]) => (
            <div className="footer-column" key={title}>
              <h3>{title}</h3>
              {links.map((link) => (
                <button key={link} onClick={() => navigate('/login')}>{link}</button>
              ))}
            </div>
          ))}
        </div>

        <div className="footer-bottom">
          {copy.footerBottom.map((item) => <span key={item}>{item}</span>)}
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
